import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Flashbar,
  Icon,
  Popover,
  Spinner,
  Table,
  TableContentSelector,
  TablePropertyFiltering,
  TablePageSizeSelector,
  TablePagination,
  TablePreferences,
  TableSorting,
} from 'AWS-UI-Components-React';
import React from 'react';
import { deepClone, isEmptyObject, downloadFile, toDownloadFormat } from '../../../utilities';
import { getJSClient } from '../../js_client';
import { MapperForm } from '../../mapper/mapper_form';
import { componentIdentity, hasMultipleComponentIdentities } from '../../../utils/cognosMultiComponent';
import { SaveCognosConfigWindow } from './save_cognos_config';
import { COGNOS_CONFIG_FIELDS } from './constants';
import { V2_PROPERTY_FILTERING_TEXT, capFilterValues, v2PropertyFilteringFunction, getFilteredItems } from '../../common/property_filter_helpers';

const MISSING_BALANCE_PLACEHOLDER = 'N/A';

// Each array's elements must equal the filter option values offered for its property.
const ARRAY_FIELD_BY_PROPERTY_KEY = { _accuracyComponentText: 'accuracyComponent', _cognosOnlyComponentText: 'cognosOnlyComponent' };
const filterAccount = (item, tokens, operation) =>
  v2PropertyFilteringFunction(item, tokens, operation, ARRAY_FIELD_BY_PROPERTY_KEY);

export class CognosConfigContainer extends React.Component {
  constructor(props) {
    super(props);
    this.client = getJSClient();
    let immutableOriginals = {};
    let accountMap = {};
    for (let cognosAccount of props.cognosConfig) {
      cognosAccount.excluded = cognosAccount.excluded || false;
      cognosAccount.comment = cognosAccount.comment || '';
      cognosAccount.modified = '';
      if (cognosAccount.excluded) {
        cognosAccount.excludedString = 'excluded';
      }
      immutableOriginals[cognosAccount.id] = deepClone(cognosAccount);
      accountMap[cognosAccount.id] = cognosAccount;
    }

    this.state = {
      accountMap: accountMap,
      accounts: props.cognosConfig,
      immutableOriginals: immutableOriginals,
      edits: {},
      hasNoEdits: true,
      // Must exactly match the attached feature children (AWS-UI v2 gate) or the table renders zero rows.
      features: ['propertyFiltering', 'pagination', 'sorting'],
      // Controlled so downloadData can replay the active filter over the full (unpaginated) set.
      filteringTokens: [],
      filteringOperation: 'and',
      saveInProgress: false,
      ignoreComponent: '',
      excludeComponent: '',
      ignoreInProgress: false,
      excludeInProgress: false,
      ignoreMessageVisible: true,
      excludeMessageVisible: true,
      ignoredAccount: 0,
      excludedAccount: 0,
      ignoreSaved: false,
      excludeSaved: false,
      saveFailed: false,
      saveCompleted: false,
      saveModalParams: { visible: false },
      saveError: '',
      options: [
        {
          value: 50,
          label: '50 resources',
        },
        {
          value: 100,
          label: '100 resources',
        },
      ],
      columnDefinitions: [
        {
          id: 'id',
          header: 'Account number',
          label: (sortState) => {
            const columnIsSorted = sortState.sortingColumn === 'id';
            const ascending = !sortState.sortingDescending;
            return `Account number, ${
              columnIsSorted ? `sorted ${ascending ? 'ascending' : 'descending'}` : 'not sorted'
            }.`;
          },
          cell: (item) => (
            <div>
              <span className="awsui-util-pr-xs">{item.id}</span>
              <span className="awsui-util-pr-xs">
                {this.checkUnmapped(item) && this.setMappingStatus(item, 'unmapped') && (
                  <Badge color={'grey'} content={'Unmapped'} />
                )}
              </span>
              <span className="awsui-util-pr-xs">
                {this.checkDoubleMapped(item) && this.setMappingStatus(item, 'double mapped') && (
                  <Badge color={'red'} content={'Double mapped'} />
                )}
              </span>
              <span className="awsui-util-pr-xs">
                {this.state.edits[item.id] && <Badge color={'blue'} content={'Modified'} />}
              </span>
              <span>{item.ignored && <Badge color={'red'} content={'Ignored'} />}</span>
            </div>
          ),
        },
        {
          id: 'detail',
          header: 'Description',
          label: (sortState) => {
            const columnIsSorted = sortState.sortingColumn === 'detail';
            const ascending = !sortState.sortingDescending;
            return `Description, ${
              columnIsSorted ? `sorted ${ascending ? 'ascending' : 'descending'}` : 'not sorted'
            }.`;
          },
          minWidth: '300px',
          cell: (item) => item.detail,
        },
        {
          id: 'excluded',
          header: 'Excluded',
          label: (sortState) => {
            const columnIsSorted = sortState.sortingColumn === 'excluded';
            const ascending = !sortState.sortingDescending;
            return `Excluded, ${columnIsSorted ? `sorted ${ascending ? 'ascending' : 'descending'}` : 'not sorted'}.`;
          },
          cell: (item) => (
            <Checkbox
              checked={item.excluded || false}
              controlId={item.id}
              onChange={this.onExclusionChange}
              disabled={this.props.readOnly}
            />
          ),
        },
        {
          id: 'ignored',
          header: 'Ignored',
          label: (sortState) => {
            const columnIsSorted = sortState.sortingColumn === 'ignored';
            const ascending = !sortState.sortingDescending;
            return `Ignored, ${columnIsSorted ? `sorted ${ascending ? 'ascending' : 'descending'}` : 'not sorted'}.`;
          },
          cell: (item) => (
            <Checkbox
              checked={item.ignored || false}
              controlId={item.id}
              onChange={this.onIgnoredChange}
              disabled={this.props.readOnly}
            />
          ),
        },
        {
          id: 'comment',
          header: 'Comment',
          minWidth: '300px',
          cell: (item) => item.comment,
        },
        {
          id: 'accuracyComponent',
          header: 'Accuracy mapping',
          cell: (item) =>
            item.accuracyComponent !== null ? item.accuracyComponent.join(', ') : item.accuracyComponent,
        },
        {
          id: 'cognosOnlyComponent',
          header: 'P&L mapping',
          cell: (item) =>
            item.cognosOnlyComponent !== null ? item.cognosOnlyComponent.join(', ') : item.cognosOnlyComponent,
        },
        {
          id: 'ytdBalance',
          header: `YTD ${new Date().getFullYear().toString()} Balance (USD)`,
          cell: (item) => this.getYTDBalanceDisplay(item),
        },
        {
          id: 'user',
          header: 'Modified by',
          cell: (item) => item.user,
        },
        {
          id: 'time',
          header: 'Modification time',
          cell: (item) => new Date(item.time).toLocaleDateString(),
        },
      ],
      hiddenColumns: ['user'],
    };
  }

  getYTDBalanceDisplay = (item) => {
    const balance = isNaN(item.ytdBalance) ? item.ytdBalance : item.ytdBalance.toLocaleString('en-US');
    const balanceDetails = this.getYTDBalanceDetails(item);
    return (
      <span>
        {balance}
        <Popover
          size="small"
          position="top"
          triggerType="text"
          dismissButton={false}
          content={<span>{balanceDetails}</span>}
        >
          {balance !== MISSING_BALANCE_PLACEHOLDER && (
            <span>
              &nbsp;<Icon name="status-info"/>
            </span>
          )}
        </Popover>
      </span>
    );
  };

  getYTDBalanceSum = (item) => {
    const { balanceInfo } = item;
    if (!balanceInfo) {
      return MISSING_BALANCE_PLACEHOLDER;
    }

    const currentYear = new Date().getFullYear().toString();
    const balance = Object.keys(balanceInfo)
      .filter((key) => key.startsWith(currentYear))
      .map((key) => balanceInfo[key])
      .reduce((a, b) => a + b, 0);

    return balance ? balance : MISSING_BALANCE_PLACEHOLDER;
  };

  getYTDBalanceDetails = (item) => {
    const { balanceInfo } = item;
    if (!balanceInfo) {
      return MISSING_BALANCE_PLACEHOLDER;
    }

    const currentYear = new Date().getFullYear().toString();

    return Object.keys(balanceInfo)
      .filter((key) => key.startsWith(currentYear))
      .sort()
      .map((key) => (
        <div className="awsui-util-font-size-0" key={key}>
          {key.substr(0, 7)} :{' '}
          {balanceInfo[key] ? balanceInfo[key].toLocaleString('en-US') : MISSING_BALANCE_PLACEHOLDER}
        </div>
      ));
  };

  setMappingStatus = (item, status) => {
    item.mappingStatus = status;
    return true;
  };

  checkUnmapped = (item) => {
    return !(
      (item.accuracyComponent && item.accuracyComponent.length) ||
      (item.cognosOnlyComponent && item.cognosOnlyComponent.length)
    ) && !item.ignored;
  };

  checkDoubleMapped = (item) => {
    // Deliberately broader than the mapper page's accuracy-only rule (see utils/cognosMultiComponent.js).
    // GMS is EXCLUDED for accuracy but INCLUDED for P&L, preserving this page's long-standing behaviour.
    // The GMS exclusion is a frontend-only convention (no backend equivalent in MappingsAccessor).
    return (
      hasMultipleComponentIdentities(item.accuracyComponent, { excludeGms: true }) ||
      hasMultipleComponentIdentities(item.cognosOnlyComponent, { excludeGms: false })
    );
  };

  getFilteringOptions = (accounts) => {
    const idOptions = new Set();
    const detailOptions = new Set();
    const accuracyOptions = new Set();
    const cognosOnlyOptions = new Set();
    const userOptions = new Set();
    (accounts || []).forEach((account) => {
      if (account.id) idOptions.add(String(account.id));
      if (account.detail) detailOptions.add(account.detail);
      if (Array.isArray(account.accuracyComponent)) {
        account.accuracyComponent.forEach((component) => component && accuracyOptions.add(component));
      }
      if (Array.isArray(account.cognosOnlyComponent)) {
        account.cognosOnlyComponent.forEach((component) => component && cognosOnlyOptions.add(component));
      }
      if (account.user) userOptions.add(account.user);
    });
    return [
      { groupValuesLabel: 'Account number Values', propertyKey: '_idText', propertyLabel: 'Account number', values: capFilterValues(Array.from(idOptions)) },
      { groupValuesLabel: 'Description Values', propertyKey: 'detail', propertyLabel: 'Description', values: capFilterValues(Array.from(detailOptions)) },
      { groupValuesLabel: 'Accuracy mapping Values', propertyKey: '_accuracyComponentText', propertyLabel: 'Accuracy mapping', values: capFilterValues(Array.from(accuracyOptions)) },
      { groupValuesLabel: 'P&L mapping Values', propertyKey: '_cognosOnlyComponentText', propertyLabel: 'P&L mapping', values: capFilterValues(Array.from(cognosOnlyOptions)) },
      // Mapping status mirrors the row Badges - lets a user isolate the red 'Double mapped' accounts (or Unmapped ones).
      // The v2 filter matches by SUBSTRING, so no status token may be a substring of another: 'Single mapped' (not 'Mapped') avoids colliding with 'Unmapped'/'Double mapped'.
      { groupValuesLabel: 'Mapping status Values', propertyKey: '_mappingStatus', propertyLabel: 'Mapping status', values: ['Double mapped', 'Unmapped', 'Single mapped'] },
      { groupValuesLabel: 'Excluded Values', propertyKey: '_searchFieldExcluded', propertyLabel: 'Excluded', values: ['excluded'] },
      { groupValuesLabel: 'Ignored Values', propertyKey: '_searchFieldIgnored', propertyLabel: 'Ignored', values: ['ignored'] },
      { groupValuesLabel: 'Modified by Values', propertyKey: 'user', propertyLabel: 'Modified by', values: capFilterValues(Array.from(userOptions)) },
    ];
  };

  _filteringOptionsMemo = { key: undefined, value: undefined };

  getMemoizedFilteringOptions = (accounts) => {
    if (this._filteringOptionsMemo.key !== accounts) {
      this._filteringOptionsMemo = { key: accounts, value: this.getFilteringOptions(accounts) };
    }
    return this._filteringOptionsMemo.value;
  };

  getAccuracyComponent = (account) => {
    const { accountMap } = this.state;
    const accuracyComponents = accountMap[account].accuracyComponent ? accountMap[account].accuracyComponent : [];
    const accuracyComponent = accuracyComponents.length > 0 ? accuracyComponents[0] : null;

    return componentIdentity(accuracyComponent);
  };

  onExclusionChange = (event) => {
    let account = event.currentTarget.component.controlId;
    let updatedValue = event.detail.checked;
    let accuracyComponent = this.getAccuracyComponent(account);
    event.detail.checked && this.setState({ excludeInProgress: true, excludedAccount: account, excludeComponent: accuracyComponent});
    !event.detail.checked && this.setState({ excludeInProgress: false});
    this.onEdit(account, COGNOS_CONFIG_FIELDS.EXCLUDED, updatedValue);
  };

  onIgnoredChange = (event) => {
    let account = event.currentTarget.component.controlId;
    let updatedValue = event.detail.checked;
    let accuracyComponent = this.getAccuracyComponent(account);
    event.detail.checked && this.setState({ ignoreInProgress: true, ignoredAccount: account, ignoreComponent: accuracyComponent});
    !event.detail.checked && this.setState({ ignoreInProgress: false});
    this.onEdit(account, COGNOS_CONFIG_FIELDS.IGNORED, updatedValue);
  };

  stageMappings = ()  => (
    (this.state.ignoreComponent.length !== 0 || this.state.excludeComponent.length !== 0) &&
    <MapperForm
      cognosConfig={this.props.cognosConfig}
      etlConfig={this.props.etlConfig}
      marketplaceConfig={this.props.marketplaceConfig}
      componentConfig={this.props.componentConfig}
      identity={this.props.identity}
      readOnly={this.props.readOnly}
      ignoreInProgress={this.state.ignoreInProgress}
      ignoredAccount={this.state.ignoredAccount}
      ignoredComponent={this.state.ignoreComponent}
      excludeInProgress={this.state.excludeInProgress}
      excludedAccount={this.state.excludedAccount}
      excludedComponent={this.state.excludeComponent}
    />
  );

  onCommentChange = (event) => {
    let account = event.currentTarget.component.controlId;
    let updatedValue = event.detail.value;
    this.onEdit(account, COGNOS_CONFIG_FIELDS.COMMENT, updatedValue);
  };

  onEdit = (account, field, updatedValue) => {
    let { edits } = this.state;
    let previousEdit = edits[account];
    let originalConfig = deepClone(this.state.immutableOriginals[account]);

    let accountFromState = this.state.accountMap[account];

    if (!previousEdit) {
      originalConfig[field] = updatedValue;
      accountFromState[field] = updatedValue;
      edits[account] = originalConfig;
    } else {
      if (!this.hasChanges(originalConfig, previousEdit, field, updatedValue)) {
        delete edits[account];
        accountFromState[field] = updatedValue;
      } else {
        edits[account][field] = updatedValue;
        accountFromState[field] = updatedValue;
      }
    }

    const key = `${field}String`;
    if (accountFromState[field]) {
      accountFromState[key] = field;
    } else {
      delete accountFromState[key];
    }

    if (edits[account]) {
      accountFromState['modified'] = 'modified';
    } else {
      accountFromState['modified'] = '';
    }

    this.setState({
      edits: edits,
      hasNoEdits: isEmptyObject(edits),
      accounts: this.state.accounts,
      saveCompleted: false,
    });
  };

  hasChanges = (
    original,
    previous,
    field,
    value,
    editableFields = [COGNOS_CONFIG_FIELDS.COMMENT, COGNOS_CONFIG_FIELDS.EXCLUDED, COGNOS_CONFIG_FIELDS.IGNORED]
  ) => {
    const valueChanged =
      typeof value === 'boolean' ? Boolean(original[field]) !== Boolean(value) : original[field] !== value;

    if (valueChanged) {
      return true;
    }

    for (let editableField of editableFields.filter((f) => f !== field)) {
      const changed =
        typeof original[editableField] === 'boolean'
          ? Boolean(original[editableField]) !== Boolean(previous[editableField])
          : original[editableField] !== previous[editableField];

      if (changed) {
        return true;
      }
    }

    return false;
  };

  cancelChanges = () => {
    let { accountMap, immutableOriginals } = this.state;
    for (let accountId in this.state.edits) {
      let account = accountMap[accountId];
      let originalVersion = immutableOriginals[accountId];

      for (let key of Object.keys(account)) {
        account[key] = originalVersion[key];
      }

      delete account['modified'];
    }
    this.setState({
      edits: {},
      hasNoEdits: true,
      accounts: this.state.accounts,
      saveCompleted: false,
    });
  };

  saveChanges = () => {
    let payload = [];
    this.setState({ saveInProgress: true, saveCompleted: false });
    const { edits } = this.state;
    for (let id in edits) {
      const item = edits[id];
      payload.push({
        ...item,
      });
    }

    const saveModalParams = {
      visible: true,
      payload,
      onSubmit: (payload) =>
        this.client.saveCognosConfigChanges(this.onSaveChangesSuccess, this.onSaveChangeFailure, { saveList: payload }),
      onCancel: this.onSaveModelDismiss,
      comment: 'Changes by user: ' + this.props.identity.username + ' at ' + new Date().toUTCString(),
    };

    this.setState({ saveModalParams });
  };

  onSaveModelDismiss = () => {
    this.setState({ saveModalParams: { visible: false }, saveInProgress: false, saveCompleted: false });
  };

  getEnrichedAccounts = (accounts) => {
    return accounts.map((account) => {
      const isDoubleMapped = this.checkDoubleMapped(account);
      const isUnmapped = this.checkUnmapped(account);
      return {
        ...account,
        ytdBalance: this.getYTDBalanceSum(account),
        // account.id is a NUMBER (JSON.parse); the v2 partial-match predicate only matches
        // string fields, so Account number filters on this coerced copy while the column still shows the number.
        _idText: String(account.id),
        _searchFieldIgnored: account.ignored ? 'ignored' : '',
        _searchFieldExcluded: account.excluded ? 'excluded' : '',
        _accuracyComponentText: Array.isArray(account.accuracyComponent) ? account.accuracyComponent.join(', ') : '',
        _cognosOnlyComponentText: Array.isArray(account.cognosOnlyComponent) ? account.cognosOnlyComponent.join(', ') : '',
        _mappingStatus: isDoubleMapped ? 'Double mapped' : isUnmapped ? 'Unmapped' : 'Single mapped',
      };
    });
  };

  downloadData = () => {
    const { columnDefinitions, accounts, filteringTokens, filteringOperation } = this.state;
    const enrichedAccounts = this.getEnrichedAccounts(accounts);
    const filteredAccounts = getFilteredItems(enrichedAccounts, filteringTokens, filteringOperation, ARRAY_FIELD_BY_PROPERTY_KEY);
    const downloadColumnDefinitions = columnDefinitions.concat([
      { id: '_mappingStatus', header: 'Mapping Status' },
    ]);
    const data = toDownloadFormat(downloadColumnDefinitions, filteredAccounts);
    downloadFile(data, 'CognosConfig.xls');
  };

  onPropertyFilteringChange = (event) => {
    this.setState({
      filteringTokens: event.detail.tokens,
      filteringOperation: event.detail.operation,
    });
  };

  onSaveChangesSuccess = (data) => {
    const savedItems = data ? data.saveList : [];

    for (let accountId in this.state.edits) {
      let updatedItem = savedItems.find((item) => item.id === accountId);
      let account = this.state.accountMap[accountId];
      let originalVersion = this.state.immutableOriginals[accountId];

      //TODO: find cleaner way and reduce the no of data structures used to maintain account state.
      account.excluded = updatedItem.excluded;
      account.comment = updatedItem.comment;
      account.ignored = updatedItem.ignored;

      originalVersion.excluded = account.excluded;
      originalVersion.comment = account.comment;
      originalVersion.ignored = account.ignored;

      delete account['modified'];
    }

    if (this.state.ignoreInProgress) {
      this.setState({ ignoreSaved: true });
    }

    if (this.state.excludeInProgress) {
      this.setState({ excludeSaved: true });
    }

    this.setState({
      saveInProgress: false,
      saveFailed: false,
      edits: {},
      hasNoEdits: true,
      saveCompleted: true,
      saveModalParams: { visible: false, payload: null, comment: null },
    });
  };

  onSaveChangeFailure = (error) => {
    console.log(error);
    this.setState({ saveInProgress: false, saveFailed: true, saveError: error, saveCompleted: true });
  };

  disableIgnoreMessage = () => {
    this.setState({ ignoreMessageVisible: false, deleteMappingNeeded: false });
  };

  disableExcludeMessage = () => {
    this.setState({ excludeMessageVisible: false, deleteMappingNeeded: false });
  };

  displayIgnoreMessage = (account) => {
    return (
      <div className="awsui-util-mb-s">
        <Alert
          onDismiss={this.disableIgnoreMessage}
          visible={this.state.ignoreMessageVisible}
          dismissAriaLabel="Close alert"
          dismissible>
          {this.state.ignoreInProgress && (
            <div> The accuracy mappings associated with the account <b>{account}</b> have been staged in Accuracy Mappings.
                  You can review <b>{this.state.ignoreComponent}</b> changes anytime from the Accuracy Mappings tab. </div> )}
        </Alert>
      </div>
    )
  };

  displayExcludeMessage = (account) => {
    return (
      <div className="awsui-util-mb-s">
        <Alert
          onDismiss={this.disableExcludeMessage}
          visible={this.state.excludeMessageVisible}
          dismissAriaLabel="Close alert"
          dismissible>
          {this.state.excludeInProgress && (
            <div> The accuracy mappings in Accuracy Benchmark associated with the excluding account <b>{account}</b> have been staged in Accuracy Mappings.
                  You can review <b>{this.state.excludeComponent}</b> changes anytime from the Accuracy Mappings tab. </div> )}
        </Alert>
      </div>
    )
  };

  render() {
    const contentSelectorOptions = [
      {
        options: this.state.columnDefinitions.map((entry) => {
          return {
            id: entry.id,
            label: entry.header,
            visible: !this.state.hiddenColumns.includes(entry.id),
          };
        }),
        label: '',
      },
    ];

    // Deliberately NOT memoized: edits mutate excluded/ignored IN PLACE on the same state.accounts
    // reference, so no sound reference key exists and a memo would serve stale checkboxes/facets.
    const accountsWithAdditionalFields = this.getEnrichedAccounts(this.state.accounts);

    return (
      <div className="cognos-config">
        {this.state.ignoreSaved && this.stageMappings()}
        {this.state.excludeSaved && this.stageMappings()}
        {this.state.ignoreSaved && this.displayIgnoreMessage(this.state.ignoredAccount)}
        {this.state.excludeSaved && this.displayExcludeMessage(this.state.excludedAccount)}
        <Table
          loadingText="Loading resources"
          columnDefinitions={this.state.columnDefinitions}
          items={accountsWithAdditionalFields}
          features={this.state.features}
          header={
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <h2> Cognos Accounts </h2>
              </div>

              {this.state.saveInProgress && (
                <div className="awsui-util-action-stripe-group">
                  <Spinner size="large" />
                </div>
              )}
              {this.state.saveFailed && (
                <div className="awsui-util-action-stripe-group">
                  <Flashbar
                    items={[
                      {
                        header: 'Saving the cognos configuration failed',
                        type: 'error',
                        content: this.state.stateError,
                        dismissible: true,
                      },
                    ]}
                  />
                </div>
              )}
              {this.state.saveCompleted && this.state.hasNoEdits && (
                <div className="awsui-util-action-stripe-group">
                  <Flashbar
                    items={[
                      {
                        header: 'Save successful',
                        type: 'success',
                        dismissible: true,
                      },
                    ]}
                  />
                </div>
              )}
              <div className="awsui-util-action-stripe-group">
                {!this.props.readOnly && (
                  <Button
                    text="Save Changes"
                    variant="primary"
                    disabled={this.state.hasNoEdits || this.state.saveInProgress}
                    onClick={this.saveChanges}
                  />
                )}
                {!this.props.readOnly && (
                  <Button
                    text="Cancel Changes"
                    disabled={this.state.hasNoEdits || this.state.saveInProgress}
                    onClick={this.cancelChanges}
                  />
                )}
                <Button
                  text="Download Data"
                  disabled={this.state.saveInProgress || !this.state.accounts}
                  onClick={this.downloadData}
                  icon="download"
                />
              </div>
            </div>
          }
          empty={<b> No cognos accounts configured. </b>}
          noMatch={<b> No matching cognos accounts. </b>}
        >
          <TablePropertyFiltering
            {...V2_PROPERTY_FILTERING_TEXT}
            filteringOptions={this.getMemoizedFilteringOptions(this.state.accounts)}
            filteringFunction={filterAccount}
            tokens={this.state.filteringTokens}
            operation={this.state.filteringOperation}
            onPropertyFilteringChange={this.onPropertyFilteringChange}
            placeholder="Filter cognos accounts"
          />
          <TablePagination pageSize={25} />
          <TableSorting
            sortingColumn="id"
            sortableColumns={[
              { id: 'id', field: 'id' },
              { id: 'excluded', field: 'excluded' },
              { id: 'ignored', field: 'ignored' },
              { id: 'time', field: 'time' },
              { id: 'ytdBalance', field: 'ytdBalance' },
            ]}
          />
          <TablePreferences title="Preferences" confirmLabel="Confirm" cancelLabel="Cancel">
            <TablePageSizeSelector title="Page size" options={this.state.options} />
            <TableContentSelector title="Select visible columns" options={contentSelectorOptions} />
          </TablePreferences>
        </Table>
        <SaveCognosConfigWindow {...this.state.saveModalParams} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/cognos_config/index.jsx